import './styles/main.scss';

import * as Sentry from '@sentry/react';
import { Loader } from 'BreetComponents';
import { appConfig } from 'BreetConfig';
import { store } from 'BreetRedux';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { ToastContainer } from 'react-toastify';

import { setupA11y } from './helpers/a11y';
import { BaseLayout } from './layout/BaseLayout';

setupA11y();

window.addEventListener('vite:preloadError', () => {
	const vitePreloadErrorCount = Number.parseInt(sessionStorage.getItem('vitePreloadErrorCount') ?? '0', 10);

	sessionStorage.setItem('vitePreloadError', 'true');
	sessionStorage.setItem('vitePreloadErrorCount', (vitePreloadErrorCount + 1).toString());
	if (vitePreloadErrorCount === 0) {
		window.location.reload();
	} else {
		console.info(`Vite preload error occurred ${vitePreloadErrorCount + 1} times, showing error boundary`);
	}
});
const {
	environment,
	isLocalMode,
	sentry: { dsn, project, enabled: isSentryEnabled },
} = appConfig;

const SENSITIVE_URL_PARAMS = ['token', 'code', 'otp', 'email'];

const scrubUrl = (url?: string | null): string | undefined => {
	if (!url) return url ?? undefined;
	try {
		const parsed = new URL(url, window.location.origin);
		let changed = false;
		for (const param of SENSITIVE_URL_PARAMS) {
			if (parsed.searchParams.has(param)) {
				parsed.searchParams.set(param, '[Filtered]');
				changed = true;
			}
		}
		return changed ? parsed.toString() : url;
	} catch {
		return url;
	}
};

const scrubSensitiveUrls = <T extends Sentry.Event>(event: T): T => {
	if (event.request?.url) event.request.url = scrubUrl(event.request.url);
	event.breadcrumbs?.forEach((crumb) => {
		const data = crumb.data;
		if (!data) return;
		if (typeof data.url === 'string') data.url = scrubUrl(data.url);
		if (typeof data.to === 'string') data.to = scrubUrl(data.to);
		if (typeof data.from === 'string') data.from = scrubUrl(data.from);
	});
	return event;
};

if (isSentryEnabled) {
	Sentry.init({
		dsn,
		environment: isLocalMode ? 'local' : environment,
		normalizeDepth: 10,
		integrations: [Sentry.browserTracingIntegration({ enableInp: true })],
		release: `${project}@${APP_VERSION}`,
		sendDefaultPii: true,
		tracesSampleRate: 0.1,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1,
		tunnel: '/tunnel',
		ignoreErrors: [
			// Error from CefSharp bots (often Microsoft Outlook SafeSearch)
			// https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
			// https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/40
			'Non-Error promise rejection captured with value: Object Not Found Matching Id:',

			// Generic browser network errors (often transient)
			'network error',
			'NetworkError',
			'Connection closed.',
			'Load failed',
			'BodyStreamBuffer was aborted',

			// Promise rejections often caused by browser extensions
			/Event `CustomEvent`/,
		],
		denyUrls: [
			// Browser extensions
			/^chrome-extension:\/\//i,
			/^moz-extension:\/\//i,
			/^safari-extension:\/\//i,
			/^webkit-masked-url:\/\//i,
		],
		allowUrls: isLocalMode ? [] : [/https:\/\/.*/i],
		beforeSend: (event) => {
			// Quick filter: drop browser extension errors
			const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
			const hasBrowserExtension = frames.some((f) =>
				/^(chrome-extension|moz-extension|safari-extension|webkit-masked-url):\/\//i.test(f.filename ?? '')
			);

			if (hasBrowserExtension) {
				return null;
			}

			return scrubSensitiveUrls(event);
		},
		beforeSendTransaction: (event) => scrubSensitiveUrls(event),
	});

	import('@sentry/react')
		.then((module) => {
			Sentry.addIntegration(
				module.replayIntegration({
					networkRequestHeaders: ['X-Breet-IP', 'x-breet-ip'],
				})
			);
		})
		.catch((error: unknown) => {
			console.error('Unable to lazy load sentry replay', error);
		});
}

const { default: router } = await import('./router');

createRoot(document.getElementById('root') as Element).render(
	<StrictMode>
		<ReduxProvider store={store}>
			<ToastContainer
				limit={1}
				className='bt_toast'
			/>
			<Suspense
				fallback={
					<BaseLayout>
						<Loader />
					</BaseLayout>
				}
			>
				<RouterProvider router={router} />
			</Suspense>
		</ReduxProvider>
	</StrictMode>
);

const initialLoader = document.getElementById('bt_initial_loader');
if (initialLoader) {
	initialLoader.remove();
}
